/**
 * The newer settings sections, and the rebuilt Themes and Typography panes.
 *
 * Kept apart from SettingsView so each section reads on its own: Chat,
 * Activity privacy, Language & time, Addons, voice processing, and the two
 * Appearance panes with a live message preview.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSession } from '@/stores/session';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Switch } from './Switch';
import { Avatar } from './Avatar';
import { FONTS, SCALES, fontById, type FontDef } from '@/lib/fonts';
import { APP_LANGUAGES } from '@/lib/i18n';
import { revealFrom } from '@/lib/reveal';
import { SCENES, type Scene } from '@/lib/themes';
import { preloadScene } from '@/lib/wallpaper';
import { AddonSlot, AddonsCard } from './Addons';
import { addAddon, listAddons, onAddonsChanged, removeAddon, saveAddons } from '@/lib/addons';
import { ADDON_LIBRARY } from '@/lib/addonLibrary';
import { TOOLKIT_ADDON } from '@/lib/toolkitAddon';
import { bridge, isDesktop } from '@/lib/bridge';
import { useToast } from './ui';
import type { Settings } from '@/lib/types';

const CARD = 'pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)] transition-shadow duration-[250ms] hover:shadow-[inset_0_0_0_1px_var(--line)]';

/* ------------------------------------------------------------ pieces */

export function Block({ title, note, children, action }: { title: string; note?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className={CARD}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="m-0 text-[16.5px] font-bold text-ink">{title}</h3>
          {note ? <p className="mt-0.5 mb-0 text-[14px] text-mute">{note}</p> : null}
        </div>
        {action}
      </div>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function Row({ icon, label, note, children }: { icon?: string; label: string; note?: string; children?: ReactNode }) {
  return (
    <div className="flex items-center gap-4 border-b border-line-soft py-3.5 last:border-b-0">
      {icon ? <Icon name={icon} size={20} className="flex-none text-mute" /> : null}
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold text-ink">{label}</div>
        {note ? <div className="mt-0.5 text-[13.5px] text-mute">{note}</div> : null}
      </div>
      {children ? <div className="flex flex-none items-center gap-2">{children}</div> : null}
    </div>
  );
}

function SwitchRow({ icon, label, note, on, onChange, disabled }: {
  icon?: string; label: string; note?: string; on: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <Row icon={icon} label={label} note={note}>
      <Switch checked={on} onChange={onChange} disabled={disabled} label={label} />
    </Row>
  );
}

function RangeRow({ label, note, min, max, step, value, display, onChange }: {
  label: string; note?: string; min: number; max: number; step: number; value: number; display: string; onChange: (v: number) => void;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="border-b border-line-soft py-3.5 last:border-b-0">
      <div className="flex items-baseline gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold text-ink">{label}</div>
          {note ? <div className="mt-0.5 text-[13.5px] text-mute">{note}</div> : null}
        </div>
        <span className="rounded-full bg-accent-soft px-2.5 py-0.5 text-[13px] font-bold text-accent tabular-nums">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full accent-[var(--accent)]"
        style={{ background: 'linear-gradient(to right, var(--accent) ' + pct + '%, var(--bg-4) ' + pct + '%)' }}
      />
    </div>
  );
}

function useSet() {
  const saveSettings = useSession((s) => s.saveSettings);
  return (patch: Partial<Settings>) => void saveSettings(patch);
}

/* ------------------------------------------------------ message preview */

/** A small conversation drawn with your real font, size and clock settings. */
export function MessagePreview() {
  const me = useSession((s) => s.me);
  const clock24 = useSession((s) => !!s.settings?.clock24);
  const at = (mins: number) => new Date(Date.now() - mins * 60000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: !clock24 });
  const bubble = 'max-w-[85%] rounded-[calc(16px*var(--radius-mul,1))] px-4 py-2.5 text-[length:var(--chat-fs,16px)] leading-[1.5]';

  return (
    <div className="pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-0 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]" aria-label="Message preview">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 flex-none place-items-center overflow-hidden rounded-full bg-surface-3">
          <img src="./logo.png" alt="" className="h-7 w-7 object-contain" />
        </span>
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-[12.5px] text-mute">{at(6)}</span>
          <p className={cn(bubble, 'm-0 bg-surface-3 text-ink')}>Up for a few games tonight?</p>
        </div>
      </div>
      <div className="mt-3 flex flex-row-reverse items-start gap-3">
        {me ? <Avatar user={me} size={36} /> : <span className="h-9 w-9 rounded-full bg-surface-3" />}
        <div className="flex min-w-0 flex-col items-end gap-1">
          <span className="text-[12.5px] text-mute">{at(4)}</span>
          <p className={cn(bubble, 'm-0 bg-accent text-accent-fg')}>Absolutely. Ranked or casual?</p>
        </div>
      </div>
      <div className="mt-3 flex items-start gap-3">
        <span className="grid h-9 w-9 flex-none place-items-center overflow-hidden rounded-full bg-surface-3">
          <img src="./logo.png" alt="" className="h-7 w-7 object-contain" />
        </span>
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-[12.5px] text-mute">{at(1)}</span>
          <p className={cn(bubble, 'm-0 bg-surface-3 text-ink')}>Casual - let&apos;s keep it chill. The quick brown fox jumps over the lazy dog.</p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ themes */

const LIGHT_THEMES = new Set(['warm', 'light', 'paper', 'sky', 'mint', 'lavender', 'peach']);

const ACCENTS: { id: string; label: string; color: string }[] = [
  { id: 'indigo', label: 'Indigo', color: '#4f46e5' },
  { id: 'violet', label: 'Violet', color: '#6557f5' },
  { id: 'blue', label: 'Blue', color: '#3e7bfa' },
  { id: 'cyan', label: 'Cyan', color: '#06b6d4' },
  { id: 'teal', label: 'Teal', color: '#14b8a6' },
  { id: 'green', label: 'Green', color: '#10b981' },
  { id: 'sage', label: 'Sage', color: '#4f7d5c' },
  { id: 'gold', label: 'Gold', color: '#e0a526' },
  { id: 'amber', label: 'Amber', color: '#c08422' },
  { id: 'orange', label: 'Orange', color: '#f59e0b' },
  { id: 'red', label: 'Red', color: '#ef4444' },
  { id: 'rose', label: 'Rose', color: '#c2506a' },
  { id: 'pink', label: 'Pink', color: '#ec4899' }
];

type ThemeRow = { id: string; name: string; bg: string; panel?: string; accent?: string; desc?: string; group?: string };

/**
 * One scene, shown as the picture of what it actually looks like.
 *
 * The card is the full reference image rather than a diagram of coloured
 * boxes, because a scene is mostly its wallpaper and no swatch can tell you
 * whether you want to live in it.
 *
 * Resting on a card starts fetching its wallpaper, so by the time somebody
 * has looked at three and picked one, the switch is instant.
 */
function SceneCard({ scene, on, busy, onPick }: {
  scene: Scene; on: boolean; busy: boolean; onPick: () => void;
}) {
  const warm = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(warm.current), []);

  const hold = () => {
    window.clearTimeout(warm.current);
    warm.current = window.setTimeout(() => void preloadScene(scene.id), 260);
  };
  const drop = () => window.clearTimeout(warm.current);

  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      onPointerEnter={hold}
      onPointerLeave={drop}
      onFocus={hold}
      onBlur={drop}
      className={cn(
        'group relative overflow-hidden rounded-[calc(16px*var(--radius-mul,1))] text-left outline-none',
        'transition-[box-shadow,transform] duration-[250ms] ease-[var(--ease-out)]',
        'hover:-translate-y-0.5 motion-reduce:hover:translate-y-0',
        'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        on
          ? 'shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_25%,transparent),0_0_20px_var(--glow),inset_0_0_0_1.5px_var(--accent)]'
          : 'shadow-[inset_0_0_0_1px_var(--line-soft)] hover:shadow-[inset_0_0_0_1px_var(--line)]',
        busy && 'opacity-70'
      )}
    >
      <img
        src={scene.thumbnail}
        alt=""
        /* Only the cards on screen are fetched and decoded; the rest of the
           grid costs nothing until it is scrolled to. */
        loading="lazy"
        decoding="async"
        draggable={false}
        className="block aspect-[16/9] w-full object-cover"
      />
      <span className="flex items-center gap-2 bg-surface-1 px-3 py-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-bold text-ink">{scene.name}</span>
          <span className="block truncate text-[12px] text-mute">{scene.blurb}</span>
        </span>
        {/* The two colours the rest of the app will be wearing. */}
        <span className="flex flex-none items-center gap-1" aria-hidden>
          <span className="h-3 w-3 rounded-full" style={{ background: scene.colors.primary }} />
          <span className="h-3 w-3 rounded-full" style={{ background: scene.colors.accent }} />
        </span>
        {on ? (
          <span className="grid h-5 w-5 flex-none place-items-center rounded-full bg-accent text-accent-fg">
            <Icon name="check" size={12} />
          </span>
        ) : null}
      </span>
    </button>
  );
}

export function ThemesPane() {
  const settings = useSession((s) => s.settings);
  const catalog = useSession((s) => s.catalog);
  const set = useSet();
  /* Which scene is being fetched, so its card can dim rather than the app
     freezing on a spinner. */
  const [fetching, setFetching] = useState<string | null>(null);

  if (!settings) return null;

  const themes = ((catalog?.themes ?? []) as ThemeRow[]).filter((t) => t.group !== 'Legacy');
  const current = settings.theme === 'dark' ? 'midnight' : settings.theme;
  const accent = settings.accent ?? 'theme';
  const currentTheme = themes.find((t) => t.id === current);
  /* A hex rather than one of the named thirteen. */
  const custom = accent.startsWith('#');

  const card = (t: ThemeRow) => {
    const on = current === t.id;
    const panel = t.panel ?? t.bg;
    const light = LIGHT_THEMES.has(t.id);
    return (
      <button
        key={t.id}
        type="button"
        aria-pressed={on}
        onClick={(e) => revealFrom(e.clientX, e.clientY, () => set({ theme: t.id, accent: 'theme' }))}
        className={cn(
          'group relative overflow-hidden rounded-[calc(16px*var(--radius-mul,1))] text-left outline-none',
          'transition-[box-shadow,transform] duration-[250ms] ease-[var(--ease-out)] hover:-translate-y-0.5 focus-visible:shadow-[0_0_0_2px_var(--accent)] motion-reduce:hover:translate-y-0',
          on ? 'shadow-[0_0_0_2px_var(--accent)]' : 'shadow-[inset_0_0_0_1px_var(--line-soft)] hover:shadow-[inset_0_0_0_1px_var(--line)]'
        )}
      >
        {/* A tiny version of the app in the theme's own colours. */}
        <span aria-hidden className="flex h-[92px] w-full gap-1.5 p-2" style={{ background: t.bg }}>
          <span className="flex w-[22%] flex-col gap-1 rounded-[6px] p-1.5" style={{ background: panel }}>
            <span className="h-1.5 w-full rounded-full" style={{ background: t.accent ?? '#6557f5', opacity: 0.9 }} />
            <span className="h-1.5 w-3/4 rounded-full" style={{ background: light ? 'rgba(0,0,0,.12)' : 'rgba(255,255,255,.14)' }} />
            <span className="h-1.5 w-2/3 rounded-full" style={{ background: light ? 'rgba(0,0,0,.12)' : 'rgba(255,255,255,.14)' }} />
          </span>
          <span className="flex flex-1 flex-col gap-1.5">
            <span className="h-[46%] rounded-[6px] p-1.5" style={{ background: panel }}>
              <span className="block h-1.5 w-1/2 rounded-full" style={{ background: light ? 'rgba(0,0,0,.18)' : 'rgba(255,255,255,.2)' }} />
              <span className="mt-1.5 block h-3 w-10 rounded-[4px]" style={{ background: t.accent ?? '#6557f5' }} />
            </span>
            <span className="flex-1 rounded-[6px]" style={{ background: panel }} />
          </span>
        </span>
        <span className="flex items-center gap-2 bg-surface-1 px-3 py-2">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-bold text-ink">{t.name}</span>
            <span className="block truncate text-[12px] text-mute">{t.desc ?? ''}</span>
          </span>
          {on ? <span className="grid h-5 w-5 flex-none place-items-center rounded-full bg-accent text-accent-fg"><Icon name="check" size={12} /></span> : null}
        </span>
      </button>
    );
  };

  const flat = themes.filter((t) => t.group !== 'Scene');
  const dark = flat.filter((t) => !LIGHT_THEMES.has(t.id));
  const light = flat.filter((t) => LIGHT_THEMES.has(t.id));
  const grid = 'grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]';

  /*
   * Picking a scene waits for its wallpaper.
   *
   * Only for a moment, and only the first time - the card has usually
   * started fetching it already. Waiting is what keeps the switch from
   * showing the new colours against the old picture and then jumping.
   */
  async function pickScene(scene: Scene) {
    setFetching(scene.id);
    await preloadScene(scene.id);
    setFetching(null);
    set({ theme: scene.id, accent: 'theme' });
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h4 className="m-0 mb-1 flex items-center gap-2 text-[14px] font-bold text-ink">
          <Icon name="image" size={16} />Scenes
        </h4>
        <p className="m-0 mb-2.5 text-[13.5px] text-mute">
          A wallpaper, a palette and a material. The picture is the scene itself.
        </p>
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(230px,1fr))]">
          {SCENES.map((s) => (
            <SceneCard
              key={s.id}
              scene={s}
              on={current === s.id}
              busy={fetching === s.id}
              onPick={() => void pickScene(s)}
            />
          ))}
        </div>
      </div>
      <div>
        <h4 className="m-0 mb-2.5 flex items-center gap-2 text-[14px] font-bold text-ink"><Icon name="moon" size={16} />Dark</h4>
        <div className={grid}>{dark.map(card)}</div>
      </div>
      <div>
        <h4 className="m-0 mb-2.5 flex items-center gap-2 text-[14px] font-bold text-ink"><Icon name="sun" size={16} />Light</h4>
        <div className={grid}>{light.map(card)}</div>
      </div>

      <div>
        <h4 className="m-0 mb-1 text-[14px] font-bold text-ink">Accent colour</h4>
        <p className="m-0 mb-3 text-[13.5px] text-mute">Buttons, highlights and switches. Theme default uses the colour the theme was made with.</p>
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            aria-pressed={accent === 'theme'}
            onClick={() => set({ accent: 'theme' })}
            className={cn(
              'flex h-10 items-center gap-2 rounded-full px-3.5 text-[13.5px] font-semibold outline-none transition-[box-shadow,background-color] duration-[250ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
              accent === 'theme' ? 'bg-accent-soft text-accent shadow-[inset_0_0_0_1.5px_var(--accent)]' : 'bg-surface-2 text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] hover:text-ink'
            )}
          >
            <span className="h-4 w-4 rounded-full" style={{ background: currentTheme?.accent ?? 'var(--accent)' }} />
            Theme default
          </button>
          {ACCENTS.map((a) => (
            <button
              key={a.id}
              type="button"
              aria-label={a.label}
              title={a.label}
              aria-pressed={accent === a.id}
              onClick={() => set({ accent: a.id })}
              className={cn(
                'grid h-9 w-9 place-items-center rounded-full outline-none transition-transform duration-[250ms] ease-[var(--ease-out)] focus-visible:shadow-[0_0_0_2px_var(--bg-1),0_0_0_4px_var(--accent)]',
                accent === a.id ? 'scale-110' : 'hover:scale-110'
              )}
              style={{ background: a.color, boxShadow: accent === a.id ? '0 0 0 2px var(--bg-2), 0 0 0 4px ' + a.color : undefined }}
            >
              {accent === a.id ? <Icon name="check" size={15} className="text-white" /> : null}
            </button>
          ))}

          {/*
            Any colour at all, not only the thirteen.

            Thirteen swatches covers most people and not the one who wants
            their crew's colour, which is most of the reason to have an
            accent in the first place. The other seven accent variables are
            derived from this one - see applyCustomAccent in the session
            store, and in particular why the text drawn on top is not
            always white.
          */}
          <label
            className={cn(
              'relative grid h-9 w-9 cursor-pointer place-items-center rounded-full outline-none transition-transform duration-[250ms] ease-[var(--ease-out)]',
              custom ? 'scale-110' : 'hover:scale-110'
            )}
            title="Any colour"
            style={{
              background: custom
                ? accent
                : 'conic-gradient(#ef4444,#f59e0b,#10b981,#06b6d4,#3e7bfa,#6557f5,#ec4899,#ef4444)',
              boxShadow: custom ? '0 0 0 2px var(--bg-2), 0 0 0 4px ' + accent : undefined
            }}
          >
            <input
              type="color"
              value={custom ? accent : (currentTheme?.accent ?? '#6557f5')}
              onChange={(e) => set({ accent: e.target.value })}
              aria-label="Any accent colour"
              className="absolute inset-0 cursor-pointer opacity-0"
            />
            {custom ? <Icon name="check" size={15} className="text-white" /> : null}
          </label>

          {custom ? (
            <span className="text-[12.5px] font-semibold text-mute tabular-nums">{accent}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- typography */

export function TypographyPane() {
  const settings = useSession((s) => s.settings);
  const set = useSet();
  if (!settings) return null;

  const scale = settings.uiScale ?? 'm';
  const desktop = isDesktop();
  const current = fontById(settings.fontFamily).id;
  const groups: FontDef['group'][] = ['Clean', 'Friendly', 'Display', 'Readable', 'Mono'];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h4 className="m-0 mb-1 text-[14px] font-bold text-ink">App size</h4>
        <p className="m-0 mb-3 text-[13.5px] text-mute">
          {desktop
            ? 'Scales everything - text, buttons and spacing - together. 100% is the default.'
            : 'In a browser, use Ctrl + and Ctrl - to change the size of everything. These sizes apply in the desktop app.'}
        </p>
        <div className="flex flex-wrap gap-1 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-1 shadow-[inset_0_0_0_1px_var(--line-soft)]">
          {SCALES.map((s) => (
            <button
              key={s.id}
              type="button"
              aria-pressed={scale === s.id}
              onClick={() => set({ uiScale: s.id as Settings['uiScale'] })}
              className={cn(
                'h-10 min-w-[64px] flex-1 rounded-[calc(12px*var(--radius-mul,1))] text-[14px] font-semibold outline-none transition-[background-color,color,box-shadow] duration-[250ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                scale === s.id ? 'bg-accent text-accent-fg shadow-[0_6px_16px_-10px_var(--accent)]' : 'text-dim hover:text-ink'
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h4 className="m-0 mb-1 text-[14px] font-bold text-ink">Typeface</h4>
        <p className="m-0 mb-3 text-[13.5px] text-mute">Used everywhere in the app. Every one is built in, so it works offline.</p>
        {groups.map((g) => (
          <div key={g} className="mb-4 last:mb-0">
            <p className="m-0 mb-2 text-[12.5px] font-bold tracking-[.08em] text-mute uppercase">{g}</p>
            <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(170px,1fr))]">
              {FONTS.filter((f) => f.group === g).map((f) => {
                const on = current === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => set({ fontFamily: f.id })}
                    className={cn(
                      'flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] p-3 text-left outline-none',
                      'transition-[box-shadow,background-color] duration-[250ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                      on ? 'bg-accent-soft shadow-[inset_0_0_0_1.5px_var(--accent)]' : 'bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-soft)] hover:shadow-[inset_0_0_0_1px_var(--line)]'
                    )}
                  >
                    <span className="grid h-11 w-11 flex-none place-items-center rounded-[10px] bg-surface-1 text-[20px] font-bold text-ink" style={{ fontFamily: f.stack }}>Aa</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14.5px] font-bold text-ink" style={{ fontFamily: f.stack }}>{f.name}</span>
                      <span className="block truncate text-[12px] text-mute">{f.note}</span>
                    </span>
                    {on ? <Icon name="check" size={16} className="flex-none text-accent" /> : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- chat */

export function ChatTab() {
  const settings = useSession((s) => s.settings);
  const set = useSet();
  if (!settings) return null;
  return (
    <>
      <Block title="How messages look" note="Changes show in the preview straight away.">
        <RangeRow
          label="Message text size"
          min={12}
          max={24}
          step={1}
          value={settings.chatFontSize ?? 16}
          display={(settings.chatFontSize ?? 16) + 'px'}
          onChange={(v) => set({ chatFontSize: v })}
        />
        <RangeRow
          label="Message spacing"
          note="How much air there is between one message and the next."
          min={0}
          max={2}
          step={0.1}
          value={settings.messageSpacing ?? 1}
          display={(settings.messageSpacing ?? 1) < 0.4 ? 'Tight' : (settings.messageSpacing ?? 1) < 0.9 ? 'Close' : (settings.messageSpacing ?? 1) < 1.4 ? 'Normal' : 'Airy'}
          onChange={(v) => set({ messageSpacing: v })}
        />
        <RangeRow
          label="Emoji-only messages"
          note="A message that is nothing but emoji is drawn this big."
          min={20}
          max={64}
          step={2}
          value={settings.emojiScale ?? 34}
          display={(settings.emojiScale ?? 34) + 'px'}
          onChange={(v) => set({ emojiScale: v })}
        />
        <div className="py-3"><MessagePreview /></div>
        <SwitchRow icon="profile" label="Avatars in chat" note="Off fits a lot more conversation on screen" on={settings.chatAvatars !== false} onChange={(v) => set({ chatAvatars: v })} />
        <SwitchRow icon="palette" label="Coloured names" note="Membership name colours, or plain text for everyone" on={settings.colorNames !== false} onChange={(v) => set({ colorNames: v })} />
        <SwitchRow icon="chat" label="Message bubbles" note="Off shows messages as a plain list under each name" on={settings.chatBubbles === true} onChange={(v) => set({ chatBubbles: v })} />
        <SwitchRow icon="list" label="Compact messages" note="More on screen, less space between them" on={!!settings.compactMessages} onChange={(v) => set({ compactMessages: v })} />
        <SwitchRow icon="clock" label="Show timestamps" note="On every message rather than on hover" on={!!settings.showTimestamps} onChange={(v) => set({ showTimestamps: v })} />
      </Block>

      <Block title="While you chat">
        <SwitchRow icon="edit" label="Typing indicators" note="See when others are typing, and let them see you" on={settings.typingIndicators !== false} onChange={(v) => set({ typingIndicators: v })} />
        <SwitchRow icon="send" label="Enter sends" note="Off means Ctrl+Enter sends and Enter makes a new line" on={settings.enterToSend !== false} onChange={(v) => set({ enterToSend: v })} />
        <SwitchRow icon="check" label="Spell check" note="Underline misspelled words while you type" on={settings.spellcheck !== false} onChange={(v) => set({ spellcheck: v })} />
      </Block>

      <Block title="Links and cards">
        <SwitchRow icon="link" label="Link previews" note="Unfurl a link into a card" on={settings.previewLinks !== false} onChange={(v) => set({ previewLinks: v })} />
        <SwitchRow icon="squads" label="Party cards" note="Show party links as a card with seats and a join button" on={settings.showEmbeds !== false} onChange={(v) => set({ showEmbeds: v })} />
      </Block>

    </>
  );
}

/* ---------------------------------------------------- activity privacy */

export function ActivityTab() {
  const settings = useSession((s) => s.settings);
  const set = useSet();
  if (!settings) return null;
  const rules = settings.statusRules;
  const setRules = (patch: Partial<typeof rules>) => set({ statusRules: { ...rules, ...patch } as typeof rules });

  return (
    <>
      <Block title="What others can see" note="About you and what you are doing right now.">
        <SwitchRow icon="gamepad" label="Share what I am playing" note="Friends see the game Pugo detects on your profile and in lists" on={settings.shareActivity !== false} onChange={(v) => set({ shareActivity: v })} />
        <SwitchRow icon="eye" label="Show when I am online" note="Off shows you as offline to everybody" on={settings.showOnlineStatus !== false} onChange={(v) => set({ showOnlineStatus: v })} />
        <SwitchRow icon="users" label="List me in the player directory" note="Off hides you from search and the players list" on={settings.listInPlayers !== false} onChange={(v) => set({ listInPlayers: v })} />
      </Block>

      <Block title="Stream mode" note="For when you are streaming or recording.">
        <SwitchRow icon="eyeOff" label="Hide personal details" note="Names, pictures and anything private stay off screen while it is on" on={!!settings.streamMode} onChange={(v) => set({ streamMode: v })} />
      </Block>

      {rules ? (
        <>
          <Block title="Away" note="Going idle when you step away from the keyboard.">
            <SwitchRow icon="moon" label="Go idle automatically" on={rules.idle.auto} onChange={(v) => setRules({ idle: { ...rules.idle, auto: v } })} />
            <RangeRow
              label="After"
              min={1}
              max={60}
              step={1}
              value={rules.idle.afterMinutes}
              display={rules.idle.afterMinutes + ' min'}
              onChange={(v) => setRules({ idle: { ...rules.idle, afterMinutes: v } })}
            />
          </Block>
          <Block title="Do not disturb" note="What a red status actually silences.">
            <SwitchRow icon="volumeOff" label="Silence sounds" on={rules.dnd.sounds === false} onChange={(v) => setRules({ dnd: { ...rules.dnd, sounds: !v } })} />
            <SwitchRow icon="bellOff" label="Hide pop-ups" on={rules.dnd.toasts === false} onChange={(v) => setRules({ dnd: { ...rules.dnd, toasts: !v } })} />
            <SwitchRow icon="chat" label="Still let direct messages through" on={rules.dnd.exceptDms} onChange={(v) => setRules({ dnd: { ...rules.dnd, exceptDms: v } })} />
          </Block>
          <Block title="Invisible">
            <SwitchRow icon="gamepad" label="Hide my game too" note="While invisible, nobody sees what you are playing" on={rules.invisible.hidePlaying} onChange={(v) => setRules({ invisible: { hidePlaying: v } })} />
          </Block>
        </>
      ) : null}

      <Block title="Friends list">
        <SwitchRow icon="friends" label="Hide offline friends" note="Only people who are around show in your lists" on={!!settings.hideOfflineFriends} onChange={(v) => set({ hideOfflineFriends: v })} />
      </Block>

      {/*
        Who may see the optional parts of you.

        Everything else on a profile is public by being on a profile. These
        four are the ones people have a view about: whether you are free to
        play, what you are playing, the games you list, and the hours behind
        them.
      */}
      <Block title="Who can see what" note="Each of these can be public, friends only, or nobody.">
        {([
          ['availability', 'Whether I am free to play'],
          ['activity', 'The game I am playing'],
          ['games', 'The games on my profile'],
          ['hours', 'My hours played']
        ] as const).map(([key, label]) => {
          const audience = settings.audience ?? { availability: 'everyone', activity: 'everyone', games: 'everyone', hours: 'everyone' };
          return (
            <div key={key} className="flex flex-wrap items-center gap-3 border-b border-line-soft py-3 last:border-b-0">
              <span className="min-w-0 flex-1 text-[15px] font-semibold text-ink">{label}</span>
              <div role="radiogroup" aria-label={label} className="flex flex-none gap-1">
                {([['everyone', 'Everyone'], ['friends', 'Friends'], ['nobody', 'Nobody']] as const).map(([id, text]) => {
                  const on = audience[key] === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => set({ audience: { ...audience, [key]: id } })}
                      className={
                        'h-9 rounded-full px-3.5 text-[12.5px] font-semibold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)] '
                        + (on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink')
                      }
                    >
                      {text}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </Block>
    </>
  );
}

/* ---------------------------------------------------- language and time */

export function LanguageTab() {
  const settings = useSession((s) => s.settings);
  const set = useSet();
  if (!settings) return null;
  const lang = settings.appLanguage ?? 'en';

  return (
    <>
      <Block title="App language" note="Menus, navigation and settings. More of the app is translated with each update.">
        <div className="mt-2 grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(180px,1fr))]">
          {APP_LANGUAGES.map((l) => {
            const on = lang === l.id;
            return (
              <button
                key={l.id}
                type="button"
                aria-pressed={on}
                onClick={() => set({ appLanguage: l.id })}
                className={cn(
                  'flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] p-3 text-left outline-none',
                  'transition-[box-shadow,background-color] duration-[250ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                  on ? 'bg-accent-soft shadow-[inset_0_0_0_1.5px_var(--accent)]' : 'bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-soft)] hover:shadow-[inset_0_0_0_1px_var(--line)]'
                )}
              >
                <span className="grid h-10 w-10 flex-none place-items-center rounded-[10px] bg-surface-1 text-[13px] font-extrabold text-accent uppercase">{l.id}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-bold text-ink">{l.native}</span>
                  <span className="block truncate text-[12.5px] text-mute">{l.name}</span>
                </span>
                {on ? <Icon name="check" size={16} className="flex-none text-accent" /> : null}
              </button>
            );
          })}
        </div>
      </Block>
      <Block title="Time">
        <SwitchRow icon="clock" label="24-hour clock" note={'Now shows ' + new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: !settings.clock24 })} on={!!settings.clock24} onChange={(v) => set({ clock24: v })} />
      </Block>
    </>
  );
}

/* ------------------------------------------------------------ voice */

/** The microphone clean-up, applied the next time the microphone starts. */
export function VoiceProcessing() {
  const settings = useSession((s) => s.settings);
  const set = useSet();
  if (!settings) return null;
  return (
    <Block title="Voice processing" note="Applied the next time your microphone starts - rejoin voice to hear the change.">
      <SwitchRow icon="waves" label="Noise suppression" note="Filters out fans, keyboards and background hum" on={settings.voiceNoise !== false} onChange={(v) => set({ voiceNoise: v })} />
      <SwitchRow icon="headset" label="Echo cancellation" note="Stops others hearing themselves through your speakers" on={settings.voiceEcho !== false} onChange={(v) => set({ voiceEcho: v })} />
      <SwitchRow icon="volume" label="Automatic volume" note="Evens out your voice when you lean in or away" on={settings.voiceAgc !== false} onChange={(v) => set({ voiceAgc: v })} />
    </Block>
  );
}

/**
 * The call bar that lives outside the app.
 *
 * Desktop only, and the section simply is not drawn in a browser - a page
 * cannot put a window over somebody's game, and a switch that explains it
 * cannot do the thing is still a switch that does nothing.
 */
export function VoiceDockSettings() {
  const [state, setState] = useState<{ onTop: boolean; snap: boolean } | null>(null);
  const desk = bridge();

  useEffect(() => {
    const dock = bridge()?.dock;
    if (!dock?.behaviour) return;
    let gone = false;
    void dock.behaviour().then((b) => { if (!gone) setState({ onTop: b.onTop, snap: b.snap }); });
    return () => { gone = true; };
  }, []);

  if (!desk?.dock?.behaviour || !state) return null;

  return (
    <Block title="Call bar" note="The small bar that stays out while you are in voice, even over a full-screen game.">
      <SwitchRow
        icon="expand"
        label="Keep it above other programs"
        note="On, it floats over games and other windows. Off, it behaves like an ordinary window and they cover it."
        on={state.onTop}
        onChange={(v) => { setState((x) => x && { ...x, onTop: v }); void bridge()?.dock?.setOnTop?.(v); }}
      />
      <SwitchRow
        icon="pin"
        label="Stick to the edges of the screen"
        note="Drop it near an edge and it clings to it, clear of the taskbar. Off, it stays exactly where you drop it."
        on={state.snap}
        onChange={(v) => { setState((x) => x && { ...x, snap: v }); void bridge()?.dock?.setSnap?.(v); }}
      />
    </Block>
  );
}

/* ------------------------------------------------------------ addons */

export function AddonsTab() {
  const toast = useToast();
  const [list, setList] = useState(() => listAddons());
  useEffect(() => onAddonsChanged(() => setList(listAddons())), []);
  const count = list.filter((a) => a.enabled).length;
  const installedAs = (name: string) => list.find((a) => a.name === name);
  const missing = ADDON_LIBRARY.filter((a) => !installedAs(a.name));

  const install = (a: { name: string; description: string; code: string }) => {
    addAddon({ name: a.name, author: 'Pugo', description: a.description, code: a.code });
  };
  /* An installed copy whose code is older than the library's gets its code
     replaced in place - its saved data (notes, sessions) stays. */
  const outdated = (a: { name: string; code: string }) => {
    const mine = installedAs(a.name);
    return !!mine && mine.code !== a.code;
  };
  const update = (a: { name: string; code: string }) => {
    saveAddons(listAddons().map((x) => (x.name === a.name ? { ...x, code: a.code } : x)));
    toast(a.name + ' updated', 'good');
  };

  return (
    <>
      <Block
        title="Addon library"
        note="Made by Pugo. Each one runs in its own sandbox and shows up on Home once installed."
        action={missing.length ? (
          <button
            type="button"
            onClick={() => { missing.forEach(install); toast(missing.length + ' addons installed', 'good'); }}
            className="flex h-10 flex-none items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-4 text-[14px] font-bold text-accent-fg outline-none transition-[background-color,transform] duration-[250ms] hover:bg-accent-hi active:scale-[.98] focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]"
          >
            <Icon name="download" size={16} />
            Install all
          </button>
        ) : undefined}
      >
        {/* Empty while the library is being rebuilt. Saying so beats an
            empty grid, which reads as something failing to load. */}
        {!ADDON_LIBRARY.length ? (
          <p className="m-0 mt-2 rounded-[var(--radius-s)] bg-surface-2 px-3.5 py-3 text-[13px] text-mute">
            Nothing on the shelf at the moment. You can still write your own below, or load one from a file.
          </p>
        ) : null}

        <div className="mt-2 grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(250px,1fr))]">
          {ADDON_LIBRARY.map((a) => {
            const mine = installedAs(a.name);
            return (
              <article key={a.id} className="flex flex-col gap-3 rounded-[calc(16px*var(--radius-mul,1))] bg-surface-2 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)] transition-shadow duration-[250ms] hover:shadow-[inset_0_0_0_1px_var(--line)]">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-accent-soft text-accent">
                    <Icon name={a.icon} size={22} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-bold text-ink">{a.name}</span>
                    <span className="block truncate text-[12.5px] text-mute">{a.tagline}</span>
                  </span>
                </div>
                <p className="m-0 flex-1 text-[13.5px] leading-snug text-dim">{a.description}</p>
                {a.party ? (
                  <span className="flex w-fit items-center gap-1.5 rounded-full bg-[color-mix(in_srgb,var(--good)_12%,transparent)] px-2.5 py-1 text-[11.5px] font-bold text-good">
                    <Icon name="users" size={12} />
                    Works in parties - live for everyone
                  </span>
                ) : null}
                <div className="flex items-center gap-2">
                  {mine ? (
                    <>
                      <span className="flex items-center gap-1.5 text-[13px] font-semibold text-good">
                        <Icon name="checkCirc" size={15} />
                        Installed
                      </span>
                      <span className="flex-1" />
                      {outdated(a) ? (
                        <button type="button" onClick={() => update(a)} className="h-9 rounded-[10px] px-3 text-[13px] font-semibold text-accent outline-none hover:bg-accent-soft focus-visible:shadow-[0_0_0_2px_var(--accent)]">Update</button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => { removeAddon(mine.id); toast(a.name + ' removed', 'good'); }}
                        className="h-9 rounded-[10px] px-3 text-[13px] font-semibold text-mute outline-none transition-colors hover:bg-surface-3 hover:text-bad focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { install(a); toast(a.name + ' installed - it is on Home now', 'good'); }}
                      className="flex h-9 items-center gap-2 rounded-[10px] bg-accent px-4 text-[13.5px] font-bold text-accent-fg outline-none transition-[background-color] duration-[250ms] hover:bg-accent-hi focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]"
                    >
                      <Icon name="plus" size={15} />
                      Install
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </Block>

      <AddonsCard />

      <Block title="For addon makers" note="Pugo Toolkit checks every part of the addon system and shows PASS or FAIL for each - use it to see what an addon can do.">
        <div className="flex flex-wrap items-center gap-3 py-2">
          <span className="grid h-10 w-10 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 text-mute"><Icon name="bug" size={19} /></span>
          <span className="min-w-[200px] flex-1 text-[14px] text-dim">Self-test, notes, a timer, dice, a team picker and a reaction game.</span>
          {installedAs('Pugo Toolkit') ? (
            <span className="text-[13px] font-semibold text-good">Installed</span>
          ) : (
            <button
              type="button"
              onClick={() => { addAddon({ name: 'Pugo Toolkit', author: 'Pugo', description: 'Self-test, notes, timer, dice, teams and a reaction game.', code: TOOLKIT_ADDON }); toast('Pugo Toolkit installed', 'good'); }}
              className="h-9 rounded-[10px] px-4 text-[13.5px] font-semibold text-ink shadow-[inset_0_0_0_1px_var(--line)] outline-none transition-colors hover:bg-surface-2 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              Install
            </button>
          )}
        </div>
      </Block>

      <Block title="Running now" note={count ? 'Your enabled addons, live. They also appear on Home.' : 'Enable an addon and it runs here.'}>
        {count ? <div className="mt-2"><AddonSlot /></div> : <p className="m-0 py-3 text-[14px] text-mute">Nothing running.</p>}
      </Block>
    </>
  );
}
